import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, Folder, FolderLock, FolderPlus, Loader2 } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { useCoffreSuiteStatus } from "@/hooks/useCoffreSuiteStatus";
import {
  createCoffreFolder,
  fetchCoffreListCached,
  prefetchCoffrePickerList,
  type CoffreEntry,
  type CoffreListZone,
} from "@/lib/suite/coffreSuiteApi";
import { getSuiteAppUrl } from "@/lib/suite/suiteApiFetch";
import { MAIL_COFFRE_UNLOCKED_EVENT, requestCoffreUnlock } from "@/lib/suite/coffreUnlock";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  filename?: string;
  title?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: (folderPath: string, zone: CoffreListZone) => void | Promise<void>;
};

export function CoffreDestFolderPickerModal({
  open,
  filename,
  title = "Enregistrer dans le Coffre",
  confirmLabel = "Enregistrer ici",
  onClose,
  onConfirm,
}: Props) {
  const { status: coffreStatus, refresh: refreshCoffreStatus } = useCoffreSuiteStatus();
  const coffreStatusRef = useRef(coffreStatus);
  coffreStatusRef.current = coffreStatus;
  const [path, setPath] = useState("");
  const [zone] = useState<CoffreListZone>("vault");
  const [entries, setEntries] = useState<CoffreEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [navigatingPath, setNavigatingPath] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [needsVault, setNeedsVault] = useState(false);
  const [creating, setCreating] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [mkdirBusy, setMkdirBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const applyLockedState = useCallback((message?: string) => {
    setNeedsVault(false);
    setLocked(true);
    setEntries([]);
    setError(
      message ?? "Le Coffre est fermé. Saisissez votre mot de passe maître pour enregistrer un fichier."
    );
  }, []);

  const reload = useCallback(
    async (nextPath: string, options: { background?: boolean; refreshStatus?: boolean } = {}) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!options.background) {
        setLoading(true);
      }
      setNavigatingPath(nextPath);
      setError(null);

      try {
        const status =
          options.refreshStatus || !coffreStatusRef.current
            ? await refreshCoffreStatus()
            : coffreStatusRef.current;
        if (controller.signal.aborted) return;
        if (!status?.ok) {
          setError(status?.message ?? "Coffre indisponible.");
          return;
        }
        if (!status.unlocked) {
          if (!status.hasVault) {
            setNeedsVault(true);
            setLocked(true);
            setEntries([]);
            setError(status.message ?? "Activez d’abord votre Coffre.");
            return;
          }
          applyLockedState(status.message);
          requestCoffreUnlock();
          return;
        }
        setLocked(false);

        const result = await fetchCoffreListCached(nextPath, "picker", controller.signal);
        if (controller.signal.aborted) return;
        if (result.error) {
          if (result.error.toLowerCase().includes("déverrouill")) {
            applyLockedState(result.error);
            requestCoffreUnlock();
            void refreshCoffreStatus();
          } else {
            setError(result.error);
            if (!options.background) setEntries([]);
          }
          return;
        }
        setPath(result.path);
        setEntries(
          result.entries.filter((entry) => entry.kind === "folder" || entry.kind === "shortcut")
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Impossible de lire le Coffre.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setNavigatingPath(null);
        }
      }
    },
    [applyLockedState, refreshCoffreStatus]
  );

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    prefetchCoffrePickerList("");
    setPath("");
    setLocked(false);
    setNeedsVault(false);
    setError(null);
    setCreating(false);
    setFolderName("");
    void reload("", { refreshStatus: true });
    // Réinitialiser uniquement à l’ouverture — pas quand le statut Coffre se rafraîchit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload volontairement exclu
  }, [open]);

  useEffect(() => {
    const onUnlock = () => {
      if (!open) return;
      void reload(path, { refreshStatus: true });
    };
    window.addEventListener(MAIL_COFFRE_UNLOCKED_EVENT, onUnlock);
    return () => window.removeEventListener(MAIL_COFFRE_UNLOCKED_EVENT, onUnlock);
  }, [open, path, reload]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const crumbs = path ? path.split("/").filter(Boolean) : [];

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(path, zone);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  }

  function openFolder(entryPath: string) {
    void reload(entryPath, { background: entries.length > 0 });
  }

  function folderNameError(raw: string): string | null {
    const name = raw.trim();
    if (!name) return "Indiquez un nom de dossier.";
    if (name.length > 200 || name === "." || name === ".." || name.startsWith(".")) {
      return "Nom de dossier invalide.";
    }
    if (/[<>:"/\\|?*\u0000-\u001F]/.test(name) || /[. ]$/.test(name)) {
      return "Nom de dossier invalide.";
    }
    return null;
  }

  async function handleCreateFolder() {
    const invalid = folderNameError(folderName);
    if (invalid) {
      setError(invalid);
      return;
    }
    setMkdirBusy(true);
    setError(null);
    try {
      const created = await createCoffreFolder(path, folderName.trim());
      if (!created.ok || !created.path) {
        setError(created.message ?? "Impossible de créer le dossier.");
        return;
      }
      setFolderName("");
      setCreating(false);
      await reload(created.path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de créer le dossier.");
    } finally {
      setMkdirBusy(false);
    }
  }

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy="coffre-picker-title"
      variant="center"
      panelClassName="mail-move-dialog max-w-lg"
    >
      <div className="flex max-h-[min(78vh,32rem)] flex-col">
        <div className="mail-move-dialog-header">
          <div className="mail-move-dialog-icon" aria-hidden>
            <FolderLock className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="coffre-picker-title" className="truncate text-sm font-semibold text-[var(--mail-text)]">
              {title}
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-[var(--mail-muted)]">
              {filename ? filename : "Choisissez le dossier de destination"}
            </p>
          </div>
          {loading && entries.length > 0 ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--mail-muted)]" aria-hidden />
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-b border-[var(--mail-border-soft)] px-4 py-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-[var(--mail-muted)]">
            <button
              type="button"
              className="rounded px-1 py-0.5 hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-accent)]"
              onClick={() => openFolder("")}
            >
              Coffre
            </button>
            {crumbs.map((part, index) => {
              const crumbPath = crumbs.slice(0, index + 1).join("/");
              return (
                <span key={crumbPath} className="inline-flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 opacity-60" aria-hidden />
                  <button
                    type="button"
                    className="rounded px-1 py-0.5 hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-accent)]"
                    onClick={() => openFolder(crumbPath)}
                  >
                    {part}
                  </button>
                </span>
              );
            })}
          </div>
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-[var(--mail-accent)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
            disabled={busy || mkdirBusy || locked || (loading && entries.length === 0)}
            onClick={() => {
              setCreating(true);
              setError(null);
              window.setTimeout(() => nameRef.current?.focus(), 0);
            }}
          >
            <FolderPlus className="h-3.5 w-3.5" aria-hidden />
            Nouveau dossier
          </button>
        </div>

        {creating ? (
          <form
            className="flex items-center gap-2 border-b border-[var(--mail-border-soft)] px-4 py-2"
            onSubmit={(event) => {
              event.preventDefault();
              void handleCreateFolder();
            }}
          >
            <input
              ref={nameRef}
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              placeholder="Nom du dossier"
              maxLength={200}
              disabled={mkdirBusy}
              className="min-w-0 flex-1 rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-1.5 text-sm text-[var(--mail-text)] outline-none focus:border-[var(--mail-accent)]"
            />
            <button
              type="submit"
              disabled={mkdirBusy || !folderName.trim()}
              className="mail-btn-primary rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50"
            >
              {mkdirBusy ? "Création…" : "Créer"}
            </button>
          </form>
        ) : null}

        {error ? (
          <p className="px-4 pt-2 text-xs text-[var(--mail-danger-text)]" role="alert">
            {error}
            {locked && !needsVault ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => requestCoffreUnlock()}
                  className="text-[var(--mail-accent)] underline"
                >
                  Saisir le mot de passe maître
                </button>
              </>
            ) : null}
            {needsVault ? (
              <>
                {" "}
                <a
                  href={getSuiteAppUrl("coffre/dist/index.html")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--mail-accent)] underline"
                >
                  Ouvrir le Coffre
                </a>
              </>
            ) : null}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--mail-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement du Coffre…
            </div>
          ) : entries.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--mail-muted)]">
              Dossier vide — enregistrez ici ou ouvrez un sous-dossier.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {entries.map((entry) => (
                <li key={entry.path}>
                  <button
                    type="button"
                    disabled={busy}
                    className={cn(
                      "mail-move-dialog-item",
                      navigatingPath === entry.path && "opacity-70",
                      busy && "pointer-events-none opacity-50"
                    )}
                    onClick={() => openFolder(entry.path)}
                    onMouseEnter={() => prefetchCoffrePickerList(entry.path)}
                  >
                    <Folder className="h-4 w-4 shrink-0 text-[var(--mail-accent)]" aria-hidden />
                    <span className="min-w-0 truncate">{entry.name}</span>
                    {navigatingPath === entry.path ? (
                      <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin text-[var(--mail-muted)]" />
                    ) : (
                      <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-[var(--mail-muted)]" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mail-move-dialog-footer gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="mail-move-dialog-cancel">
            Annuler
          </button>
          <button
            type="button"
            className="mail-btn-primary rounded-full px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            onClick={() => void handleConfirm()}
            disabled={busy || mkdirBusy || locked || (loading && entries.length === 0)}
          >
            {busy ? "Enregistrement…" : confirmLabel}
          </button>
        </div>
      </div>
    </MailModalOverlay>
  );
}
